import React, { useState, useEffect } from 'react';
import NewTenantForm from './new_tenant_form.js.jsx';
import LeaseDetailsForm from './lease_details_form.js.jsx';
import ScheduleForm from './schedule_form.js.jsx';
import { useGlobalContext } from './components/GlobalContextProvider';
import { getRailsIdFromRelayId } from './components/Tools';

const NewLeaseModal = ({
  unit = {},
  lease = {},
  leaseProfile = {},
  tenantRoles = [],
  securityDepositItems = [],
  country = '',
  state = '',
  origin = '',
  reinstate = false,
  emailTemplates = [],
  feeStructures = [],
  documentTemplates = [],
  leaseCustomFields = [],
  customFieldTypeGroups = [],
  initialData = {},
  incomeOptions = [],
  transactionSchedule = {},
  newTenantLightboxData = {},
  tenantTermOverride = 'Tenant',
  leaseTermOverride = 'Lease',
  parsedScheduleData = null,
}) => {
  const initialStep = reinstate ? 'lease' : 'tenant';
  const [currentStep, setCurrentStep] = useState(initialStep);

  useEffect(() => {
    const container = document.getElementById('new-lease-lightbox-redesign');
    window.mdc.autoInit(container);
    window.attachFormatMoneyListener();
    window.initDatepicker('#new-lease-lightbox-redesign');
    window.initDateTimepicker('#new-lease-lightbox-redesign');
    window.initSelect2('#new-lease-lightbox-redesign');

    if (window.setDefaultCountry) {
      window.setDefaultCountry(country);
    }
    if (window.setDefaultState) {
      window.setDefaultState(state);
    }
  }, []);

  useEffect(() => {
    const container = document.getElementById('new-lease-lightbox-redesign');
    if (container) {
      container.removeEventListener('change', handleAddressCheckboxChange);
      container.addEventListener('change', handleAddressCheckboxChange);
    }

    return () => {
      if (container) {
        container.removeEventListener('change', handleAddressCheckboxChange);
      }
    };
  }, [currentStep]);

  const handleAddressCheckboxChange = (event) => {
    if (event.target.classList.contains('use_building_address')) {
      const tenantNumber =
        event.target.id.split('-')[1]?.replace(/[\[\]']+/g, '') || '0';
      if (event.target.checked) {
        insertBuildingAddress(tenantNumber);
      } else {
        clearBuildingAddress(tenantNumber);
      }
    }
  };

  const insertBuildingAddress = (tenantNumber) => {
    const container = document.getElementById('new-lease-lightbox-redesign');
    if (!container) return;

    const addressline1 = container.querySelector(
      `[id$="${tenantNumber}_tenant_attributes_address_attributes_addressline1"]`
    );
    if (addressline1) {
      addressline1.value = unit.address?.addressline1 || '';
      const label = addressline1.parentElement?.querySelector('label');
      if (label && addressline1.value !== '') {
        label.classList.add('mdc-floating-label--float-above');
      }
    }

    const addressline2 = container.querySelector(
      `[id$="${tenantNumber}_tenant_attributes_address_attributes_addressline2"]`
    );
    if (addressline2) {
      addressline2.value = unit.address?.addressline2 || unit.number || '';
      const label = addressline2.parentElement?.querySelector('label');
      if (label && addressline2.value !== '') {
        label.classList.add('mdc-floating-label--float-above');
      }
    }

    const city = container.querySelector(
      `[id$="${tenantNumber}_tenant_attributes_address_attributes_city"]`
    );
    if (city) {
      city.value = unit.address?.city || '';
      const label = city.parentElement?.querySelector('label');
      if (label && city.value !== '') {
        label.classList.add('mdc-floating-label--float-above');
      }
    }

    const zipcode = container.querySelector(
      `[id$="${tenantNumber}_tenant_attributes_address_attributes_zipcode"]`
    );
    if (zipcode) {
      zipcode.value = unit.address?.zipcode || '';
      const label = zipcode.parentElement?.querySelector('label');
      if (label && zipcode.value !== '') {
        label.classList.add('mdc-floating-label--float-above');
      }
    }

    const country = container.querySelector(
      `[id$="${tenantNumber}_tenant_attributes_address_attributes_country"]`
    );
    if (country) {
      country.value = unit.address?.country || '';
      const changeEvent = new Event('change', { bubbles: true });
      country.dispatchEvent(changeEvent);
    }

    const state = container.querySelector(
      `[id$="${tenantNumber}_tenant_attributes_address_attributes_state"]`
    );
    if (state) {
      state.value = unit.address?.state || '';
      const changeEvent = new Event('change', { bubbles: true });
      state.dispatchEvent(changeEvent);
    }
  };

  const clearBuildingAddress = (tenantNumber) => {
    const container = document.getElementById('new-lease-lightbox-redesign');
    if (!container) return;

    const fields = ['addressline1', 'addressline2', 'city', 'zipcode'];
    fields.forEach((field) => {
      const input = container.querySelector(
        `[id$="${tenantNumber}_tenant_attributes_address_attributes_${field}"]`
      );
      if (input) {
        input.value = '';
        const label = input.parentElement?.querySelector('label');
        if (label) {
          label.classList.remove('mdc-floating-label--float-above');
        }
      }
    });

    const country = container.querySelector(
      `[id$="${tenantNumber}_tenant_attributes_address_attributes_country"]`
    );
    if (country) {
      const changeEvent = new Event('change', { bubbles: true });
      country.dispatchEvent(changeEvent);
    }

    const state = container.querySelector(
      `[id$="${tenantNumber}_tenant_attributes_address_attributes_state"]`
    );
    if (state) {
      const changeEvent = new Event('change', { bubbles: true });
      state.dispatchEvent(changeEvent);
    }
  };

  const goToTenantStep = () => setCurrentStep('tenant');
  const goToLeaseStep = () => setCurrentStep('lease');
  const goToScheduleStep = () => setCurrentStep('schedule');

  const handleSubmit = () => {
    const form = document.getElementById('submit-lease-redesign');
    if (form) {
      if (reinstate) {
        const submitButton = document.getElementById('reinstate-lease-btn');
        if (submitButton) submitButton.click();
      } else {
        const submitButton = document.getElementById('lease-submit-button');
        if (submitButton) submitButton.click();
      }
    }
  };

  const handleCancel = () => {
    const cancelButton = document.querySelector(
      '.mdc-dialog__footer__button--cancelPM'
    );
    if (cancelButton) cancelButton.click();
  };

  const renderStepIndicator = () => {
    if (reinstate) return null;

    let stepText = '';
    if (currentStep === 'tenant') {
      stepText = origin === 'properties' ? '1/3' : '2/4';
    } else if (currentStep === 'lease') {
      stepText = origin === 'properties' ? '2/3' : '3/4';
    } else if (currentStep === 'schedule') {
      stepText = origin === 'properties' ? '3/3' : '4/4';
    }

    return <span className="pm-modalstep">{stepText}</span>;
  };

  const renderAllStepContent = () => {
    const parseGroups = (raw) =>
      raw.map(([label, options]) => ({
        label,
        options: options.map(([optLabel, value, data]) => ({
          label: optLabel,
          value,
          data,
        })),
      }));

    const tenantProps = {
      tenantTermOverride,
      leaseTermOverride,
      origin,
      newTenantLightboxData: {
        ...newTenantLightboxData,
        tenantRoles,
        unit,
        lease,
        leaseProfile,
      },
    };

    const leaseProps = {
      emailTemplates,
      feeStructures,
      securityDepositItems,
      documentTemplates,
      leaseCustomFields,
      customFieldTypeGroups,
      initialData,
      reinstate,
      origin,
      tenantTermOverride,
      leaseTermOverride,
    };

    const scheduleProps = {
      incomeOptions: parseGroups(incomeOptions || []),
      initialData: {
        name: transactionSchedule.name,
        startDate: transactionSchedule.startDate,
        endDate: transactionSchedule.endDate,
        monthToMonth:
          transactionSchedule.monthToMonth === 'true' ||
          transactionSchedule.monthToMonth === true,
        scheduleType: transactionSchedule.scheduleType,
        daysForPayment: transactionSchedule.daysForPayment,
        dueDate: transactionSchedule.dueDate,
        itemId: transactionSchedule.itemId,
        amount: transactionSchedule.amount,
        daysAdvanceGenerateCharge:
          transactionSchedule.daysAdvanceGenerateCharge,
        daysAdvanceGenerateChargeType:
          transactionSchedule.daysAdvanceGenerateChargeType,
        dueDateType: transactionSchedule.dueDateType,
      },
      parsedScheduleData,
    };

    return (
      <>
        {!reinstate && (
          <div
            id="new-tenant-form"
            style={{ display: currentStep === 'tenant' ? 'block' : 'none' }}
          >
            <NewTenantForm {...tenantProps} />
          </div>
        )}

        <div
          id="new-lease-form"
          style={{ display: currentStep === 'lease' ? 'block' : 'none' }}
        >
          <LeaseDetailsForm {...leaseProps} />
        </div>

        <div
          id="new-schedule-form"
          style={{ display: currentStep === 'schedule' ? 'block' : 'none' }}
        >
          <ScheduleForm {...scheduleProps} />
        </div>
      </>
    );
  };

  const renderFooterButtons = () => {
    const buttons = [];

    if (!reinstate && currentStep !== 'tenant') {
      const backHandler =
        currentStep === 'lease' ? goToTenantStep : goToLeaseStep;
      buttons.push(
        <button
          key="back"
          type="button"
          className="mdc-button back-button pm-text-color--blue1 mdc-dialog__footer__button"
          onClick={backHandler}
        >
          Back
        </button>
      );
    }

    buttons.push(
      <button
        key="cancel"
        type="button"
        id="new-lease-cancel-button"
        className="mdc-button pm-text-color--grey5 mdc-dialog__footer__button mdc-dialog__footer__button--cancelPM"
        onClick={handleCancel}
      >
        Cancel
      </button>
    );

    if (currentStep === 'schedule') {
      buttons.push(
        <button
          key="submit"
          type="button"
          className="mdc-button pm-text-color--blue1 mdc-dialog__footer__button mdc-dialog__footer__button--acceptPM"
          id={reinstate ? 'reinstate-lease-btn' : 'lease-submit-button'}
          onClick={handleSubmit}
        >
          {reinstate ? 'Reinstate' : 'Save'}
        </button>
      );
    } else {
      const nextHandler =
        currentStep === 'tenant' ? goToLeaseStep : goToScheduleStep;
      buttons.push(
        <button
          key="next"
          type="button"
          className="mdc-button next-button pm-text-color--blue1 mdc-dialog__footer__button"
          onClick={nextHandler}
        >
          Next
        </button>
      );
    }

    return buttons;
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'tenant':
        return `New ${leaseTermOverride}`;
      case 'lease':
        return `${leaseTermOverride} Details`;
      case 'schedule':
        return 'Schedule Details';
      default:
        return '';
    }
  };

  const dataAttributes = {};
  if (!LIVE_PRODUCTION_ENVIRONMENT) {
    console.log('unit', unit);
    dataAttributes.unitNumber = unit.number || '';
    dataAttributes.buildingName = unit.building?.name || '';
  }

  return (
    <aside
      id="mdc-dialog-newtenant2"
      className="mdc-dialog mdc-dialog--open"
      role="alertdialog"
      aria-labelledby="mdc-dialog-default-label"
      aria-describedby="mdc-dialog-default-description"
    >
      <div
        className="mdc-dialog__surface"
        id="new-lease-lightbox-redesign"
        {...dataAttributes}
      >
        <form
          id="submit-lease-redesign"
          action={
            reinstate
              ? `/leases/${lease.id}/update_redesign`
              : '/create_lease_redesign'
          }
          method="post"
        >
          {!reinstate && (
            <>
              <input type="hidden" name="lease[unit_id]" value={unit.id} />
              <input
                type="hidden"
                name="lease[building_id]"
                value={unit.building?.id}
              />
              <input
                type="hidden"
                name="lease[allowed_payment_types]"
                value={
                  lease.allowed_payment_types ||
                  leaseProfile.allowed_payment_types
                }
              />
              <input
                type="hidden"
                name="lease[allow_online_payments]"
                value={
                  lease.allow_online_payments ||
                  leaseProfile.allow_online_payments
                }
              />
            </>
          )}

          {reinstate && (
            <>
              <input type="hidden" name="lease[move_out_date]" value="" />
              <input type="hidden" name="lease[responsibility_date]" value="" />
              <input type="hidden" name="lease[unit_id]" value={unit.id} />
              <input
                type="hidden"
                name="lease[prorate_first_month_rent]"
                value={
                  lease.prorate_first_month_rent ||
                  leaseProfile.prorate_first_month_rent
                }
              />
            </>
          )}

          <header className="mdc-dialog__header pm-bg-color--blue1">
            <h2
              id="mdc-dialog-default-label"
              className="mdc-dialog__header__title"
            >
              {getStepTitle()}
            </h2>
            {renderStepIndicator()}
          </header>

          <section
            id="mdc-dialog-default-description"
            className="mdc-dialog__body mdc-dialog__body--scrollable"
          >
            {renderAllStepContent()}
          </section>

          <footer className="mdc-dialog__footer">
            {renderFooterButtons()}
          </footer>
        </form>
      </div>
      <div className="mdc-dialog__backdrop"></div>
    </aside>
  );
};

export const handleSubmitNewLease = (
  closeDialogCallback,
  termOverrides = {},
  aiOptions = {}
) => {
  const tenantTermPlural = termOverrides.tenantTermPlural || 'tenants';
  const tenantTerm = termOverrides.tenantTerm || 'tenant';
  const leaseTerm = termOverrides.leaseTerm || 'lease';
  const rentTerm = termOverrides.rentTerm || 'rent';
  const aiAttachmentId = aiOptions.aiAttachmentId;
  const onSuccess = aiOptions.onSuccess;

  startLoadingSpinner('new-lease-lightbox-redesign');

  let baseFormData = $('#submit-lease-redesign').serialize();
  let attachmentParams = aiAttachmentId
    ? `&lease[attachment_parent_relationships_attributes][0][attachment_id]=${getRailsIdFromRelayId(aiAttachmentId)}&lease[attachment_parent_relationships_attributes][0][ai_generated]=true`
    : '';

  let formData = baseFormData + attachmentParams;

  $.ajax({
    url: $('#submit-lease-redesign').attr('action'),
    type: 'POST',
    data: formData,
    dataType: 'JSON',
    success: function (data) {
      const successCallback = (data) => {
        if (data.errors == null) {
          closeDialogCallback();
          stopLoadingSpinner('new-lease-lightbox-redesign');

          if (onSuccess && data.primary_tenant_entity_id) {
            onSuccess(data.primary_tenant_entity_id);
          }

          if (data.confirmPosted) {
            confirm(
              'Would you like to remove the posted listings for this unit?',
              (ok) => {
                if (ok) {
                  $.ajax({
                    url: '/units/' + data.unit_id + '/unpost_unit',
                    type: 'POST',
                    success: function () {
                      showSnackbarMessage(
                        'Unit has been successfully unposted',
                        'pm-main-section'
                      );
                    },
                  });
                }
              }
            );
          }
          if (data.automatic_action_errors) {
            alert(data.automatic_action_errors);
          }
        } else {
          stopLoadingSpinner('new-lease-lightbox-redesign');
          alert(data.errors);
        }
      };

      var text = '';
      if (data.newDaysInAdvanceType == 'relative') {
        text =
          'The transaction schedule will be updated to generate charges ' +
          data.newDaysInAdvance +
          " days in advance. A charge for the scheduled date '" +
          data.nextScheduledDate +
          "' may not have been generated yet, and it is less than " +
          data.newDaysInAdvance +
          ' days away. Would you like to create a charge for ' +
          data.nextScheduledDate +
          ' now?';
      } else {
        text =
          'The transaction schedule will be updated to generate charges on day ' +
          data.newDaysInAdvance +
          ". A charge for the scheduled date '" +
          data.nextScheduledDate +
          "' may not have been generated yet, and the expected date to generate the charge " +
          data.generateDate +
          ' has past. Would you like to create a charge for ' +
          data.nextScheduledDate +
          ' now?';
      }

      let generateMissingChargeCallback = null;
      if (data.skipsCharge && data.generateMissingCharge == null) {
        stopLoadingSpinner('new-lease-lightbox-redesign');
        generateMissingChargeCallback = function () {
          confirm(text, (ok) => {
            if (ok) {
              startLoadingSpinner('new-lease-lightbox-redesign');
              $.ajax({
                type: 'POST',
                url: $('#submit-lease-redesign').attr('action'),
                data:
                  $('#submit-lease-redesign').serialize() +
                  '&confirm_zero_schedule=true&confirm_tenant_name=true&confirm_no_online_activation=true&generate_missing_charge=yes' +
                  attachmentParams,
                dataType: 'JSON',
                success: function (newData) {
                  stopLoadingSpinner('new-lease-lightbox-redesign');
                  successCallback(newData);
                },
              });
            } else {
              startLoadingSpinner('new-lease-lightbox-redesign');
              $.ajax({
                type: 'POST',
                url: $('#submit-lease-redesign').attr('action'),
                data:
                  $('#submit-lease-redesign').serialize() +
                  '&confirm_zero_schedule=true&confirm_tenant_name=true&confirm_no_online_activation=true&generate_missing_charge=no' +
                  attachmentParams,
                dataType: 'JSON',
                success: function (newData) {
                  stopLoadingSpinner('new-lease-lightbox-redesign');
                  successCallback(newData);
                },
              });
            }
          });
        };
      }

      let noOnlineActivationCallback = null;
      if (data.confirmNoOnlineActivation) {
        noOnlineActivationCallback = function (data, successCallback) {
          stopLoadingSpinner('new-lease-lightbox-redesign');
          const tenantNames = data.tenantsWithNoOnlineAccess
            .map((tenant) => tenant.tenant_name)
            .join(data.tenantsWithNoOnlineAccess.length > 2 ? ', ' : ' and ')
            .replace(/, ([^,]*)$/, ', and $1');
          const confirm_online_activation_message =
            tenantNames +
            (data.tenantsWithNoOnlineAccess.length === 1 ? ' does' : ' do') +
            ' not have online access enabled. Do you want to send them a message inviting them to the ' +
            tenantTerm +
            ' portal?';
          confirm(confirm_online_activation_message, (ok) => {
            if (ok) {
              data.tenantsWithNoOnlineAccess.forEach((tenant) => {
                const checkboxId = `allow_online_access_${tenant.index}`;
                const checkboxElement = document.getElementById(checkboxId);
                if (checkboxElement) {
                  checkboxElement.checked = true;
                }
              });
            }
            startLoadingSpinner('new-lease-lightbox-redesign');
            $.ajax({
              type: 'POST',
              url: $('#submit-lease-redesign').attr('action'),
              data:
                $('#submit-lease-redesign').serialize() +
                '&confirm_zero_schedule=true&confirm_tenant_name=true&confirm_no_online_activation=true' +
                attachmentParams,
              dataType: 'JSON',
              success: function (newData) {
                stopLoadingSpinner('new-lease-lightbox-redesign');
                if (generateMissingChargeCallback != null) {
                  generateMissingChargeCallback();
                } else {
                  successCallback(newData);
                }
              },
            });
          });
        };
      }

      let tenantNameCallback = null;
      if (data.confirmTenantName) {
        stopLoadingSpinner('new-lease-lightbox-redesign');
        tenantNameCallback = function () {
          confirm(
            'An entity with the same name as this ' +
              tenantTerm +
              ' already exists. Are you sure you want to create this ' +
              leaseTerm +
              "?\n\n(View <a href='https://help.propertymatrix.com/en/articles/65307-converting-a-lead-to-a-tenant-or-moving-in-existing-tenants' target='_blank'>this article</a> to see how to connect the existing entity with this " +
              leaseTerm +
              '.)',
            (ok) => {
              if (ok) {
                startLoadingSpinner('new-lease-lightbox-redesign');
                $.ajax({
                  type: 'POST',
                  url: $('#submit-lease-redesign').attr('action'),
                  data:
                    $('#submit-lease-redesign').serialize() +
                    '&confirm_zero_schedule=true&confirm_tenant_name=true' +
                    attachmentParams,
                  dataType: 'JSON',
                  success: function (newData) {
                    stopLoadingSpinner('new-lease-lightbox-redesign');
                    if (generateMissingChargeCallback != null) {
                      generateMissingChargeCallback();
                    } else if (data.confirmNoOnlineActivation) {
                      noOnlineActivationCallback(data, successCallback);
                    } else {
                      successCallback(newData);
                    }
                  },
                });
              } else {
                stopLoadingSpinner('new-lease-lightbox-redesign');
              }
            }
          );
        };
      }

      if (data.confirm_zero_schedule_flag) {
        stopLoadingSpinner('new-lease-lightbox-redesign');
        confirm(
          'A $0 ' +
            rentTerm +
            ' schedule has been submitted. Are you sure you want to create this ' +
            leaseTerm +
            '?',
          (ok) => {
            if (ok) {
              startLoadingSpinner('new-lease-lightbox-redesign');
              $.ajax({
                type: 'POST',
                url: $('#submit-lease-redesign').attr('action'),
                data:
                  $('#submit-lease-redesign').serialize() +
                  '&confirm_zero_schedule=true' +
                  attachmentParams,
                dataType: 'JSON',
                success: function (newData) {
                  stopLoadingSpinner('new-lease-lightbox-redesign');
                  if (tenantNameCallback != null) {
                    tenantNameCallback();
                  } else if (generateMissingChargeCallback != null) {
                    generateMissingChargeCallback();
                  } else if (data.confirmNoOnlineActivation) {
                    noOnlineActivationCallback(data, successCallback);
                  } else {
                    successCallback(newData);
                  }
                },
              });
            }
          }
        );
      } else if (data.confirmTenantName) {
        tenantNameCallback();
      } else if (data.confirmNoOnlineActivation) {
        noOnlineActivationCallback(data, successCallback);
      } else if (data.skipsCharge && data.generateMissingCharge == null) {
        generateMissingChargeCallback();
      } else {
        successCallback(data);
      }
    },
    complete: function () {
      enableSubmit('#submit-lease-redesign #lease-submit-button');
    },
  });
};

export default NewLeaseModal;
